import React, {memo} from 'react';
import NextLink from 'next/link';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '@/components/Ui-components/display-image';
import {
    colors,
    borderRadius,
    fontSize,
    fontWeight,
    letterSpacing,
    lineHeight,
    spacing,
    shadows,
    overlay,
    transition
} from '@/styles/tokens';
import Eyebrow from '@/components/Common/Eyebrow';

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface PricingTier {
    tier: string;
    price: string;
    bestFor: string;
    features: string[];
    buttonText: string;
    featured?: boolean;
    href?: string;
    external?: boolean;
}

export interface TrustItem {
    icon: string;
    title: string;
    body: string;
}

export interface PricingSectionProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    tiers: PricingTier[];
    trustItems?: TrustItem[];
    openForm?: () => void;
}

// ─── sx constants ────────────────────────────────────────────────────────────────

const lightCardSx = {
    bgcolor: colors.background.default,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius['3xl'],
    p: spacing[28],
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    transition: `transform ${transition.medium}, box-shadow ${transition.medium}, border-color ${transition.medium}`,
    '&:hover': {
        transform: 'translateY(-6px)',
        boxShadow: shadows.elevated,
        border: `1px solid ${colors.primary.main}`
    }
} as const;

const darkCardSx = {
    background: `linear-gradient(to left, ${colors.primary.main}, ${colors.primary.midnight})`,
    border: `1px solid ${overlay.primaryBorder}`,
    borderRadius: borderRadius['3xl'],
    p: spacing[40],
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    boxShadow: shadows.sm
} as const;

const tierLabelLightSx = {
    fontWeight: fontWeight.semibold,
    fontSize: fontSize[14],
    color: colors.text.muted,
    letterSpacing: letterSpacing.wide,
    mb: spacing[12],
    textTransform: 'uppercase' as const
} as const;

const tierLabelDarkSx = {
    ...tierLabelLightSx,
    color: colors.text.inverse
} as const;

const priceLightSx = {
    fontWeight: fontWeight.bold,
    fontSize: {xs: fontSize[28], sm: fontSize[32]},
    color: colors.text.primary,
    lineHeight: lineHeight[48]
} as const;

const priceDarkSx = {
    ...priceLightSx,
    color: colors.text.inverse
} as const;

const bestForLightSx = {
    fontSize: fontSize[14],
    color: colors.primary.main,
    lineHeight: lineHeight[20],
    minHeight: spacing[40],
    mb: spacing[24]
} as const;

const bestForDarkSx = {
    ...bestForLightSx,
    color: colors.accent.orange
} as const;

const featureTextLightSx = {
    fontSize: fontSize[16],
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

const featureTextDarkSx = {
    fontSize: fontSize[16],
    color: colors.text.inverse,
    lineHeight: lineHeight[28]
} as const;

// Shrink button text up to 1100px so labels stay on one line; back to normal size above 1100px
const btnResponsiveFontSx = {
    '@media (max-width:1100px)': {fontSize: fontSize[12]}
} as const;

const lightBtnSx = {
    bgcolor: colors.primary.main,
    color: colors.text.inverse,
    borderRadius: borderRadius.lg,
    py: spacing[16],
    fontWeight: fontWeight.bold,
    fontSize: fontSize[16],
    textTransform: 'none',
    mt: 'auto',
    ...btnResponsiveFontSx,
    '&:hover': {bgcolor: colors.primary.deep}
} as const;

const orangeBtnSx = {
    bgcolor: colors.accent.orange,
    color: colors.text.inverse,
    borderRadius: borderRadius.lg,
    py: spacing[16],
    fontWeight: fontWeight.bold,
    fontSize: fontSize[16],
    textTransform: 'none',
    mt: 'auto',
    boxShadow: shadows.lg,
    ...btnResponsiveFontSx,
    '&:hover': {bgcolor: colors.accent.orangeDark}
} as const;

const popularBadgeSx = {
    position: 'absolute',
    top: `-${spacing[16]}`,
    left: '50%',
    transform: 'translateX(-50%)',
    bgcolor: colors.accent.orange,
    borderRadius: borderRadius.lg,
    px: spacing[16],
    py: spacing[8],
    whiteSpace: 'nowrap'
} as const;

const trustStripSx = {
    bgcolor: colors.background.default,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.xl,
    p: spacing[32],
    boxShadow: shadows.lg,
    display: 'grid',
    gridTemplateColumns: {xs: '1fr 1fr', md: 'repeat(4, 1fr)'},
    gap: spacing[24],
    mt: spacing[32]
} as const;

const iconWrapSx = {
    width: spacing[48],
    height: spacing[48],
    bgcolor: colors.background.slate50,
    borderRadius: borderRadius.lg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mb: spacing[16],
    flexShrink: 0,
    transition: 'background-color 0.25s ease'
} as const;

const gridItemFlexSx = {
    display: 'flex',
    // Below 1100px the 4-up row gets cramped — force 2 per row, then 1 per row on mobile
    '@media (max-width:1100px)': {flexBasis: '50%', maxWidth: '50%'},
    '@media (max-width:600px)': {flexBasis: '100%', maxWidth: '100%'}
} as const;

const darkCardFullWidthSx = {
    ...darkCardSx,
    width: '100%'
} as const;

const lightCardFullWidthSx = {
    ...lightCardSx,
    width: '100%'
} as const;

const popularBadgeTextSx = {
    fontWeight: fontWeight.bold,
    fontSize: fontSize[12],
    color: colors.text.inverse,
    letterSpacing: letterSpacing.wider
} as const;

const trustDividerSx = {
    display: {xs: 'none', md: 'block'},
    position: 'absolute',
    right: 0,
    top: '20%',
    bottom: '20%',
    width: '1px',
    bgcolor: colors.neutral.borderLight
} as const;

const trustItemTitleSx = {
    fontWeight: fontWeight.semibold,
    fontSize: fontSize[14],
    color: colors.text.primary,
    mb: spacing[4],
    lineHeight: lineHeight[20]
} as const;

const trustItemBodySx = {
    fontSize: fontSize[12],
    color: colors.text.muted,
    lineHeight: lineHeight[20]
} as const;

const trustItemContentSx = {
    borderRadius: borderRadius.lg,
    p: spacing[12],
    transition: 'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease',
    '&:hover': {
        transform: 'translateY(-5px)',
        backgroundColor: colors.primary.light,
        boxShadow: shadows.lg,
        '& > div:first-of-type': {
            backgroundColor: colors.primary.iconBg
        }
    }
} as const;

// ─── Sub-components ──────────────────────────────────────────────────────────────

const FeatureItem = ({text, dark}: {text: string; dark?: boolean}) => (
    <Box display="flex" gap={spacing[12]} alignItems="flex-start">
        <Box
            flexShrink={0}
            sx={{
                width: spacing[16],
                height: spacing[16],
                mt: spacing[4],
                color: dark ? colors.text.inverse : colors.primary.main,
                fontSize: fontSize[14],
                fontWeight: fontWeight.bold,
                lineHeight: lineHeight[20]
            }}
        >
            ✓
        </Box>
        <Typography className="font-regular-text" sx={dark ? featureTextDarkSx : featureTextLightSx}>
            {text}
        </Typography>
    </Box>
);

// ─── Main component ──────────────────────────────────────────────────────────────

// Resolve button behaviour: internal link (next/link), external link (new tab), or form trigger
const getButtonProps = (tier: PricingTier, openForm?: () => void) => {
    if (tier.href) {
        if (tier.external) {
            return {onClick: () => globalThis.open(tier.href, '_blank', 'noopener,noreferrer')} as const;
        }
        return {component: NextLink, href: tier.href, target: '_blank', rel: 'noopener noreferrer'} as const;
    }
    return {onClick: openForm} as const;
};

const PricingSection = ({eyebrow, heading, subheading, tiers, trustItems, openForm}: PricingSectionProps) => {
    // Distribute cards evenly per row: 1→12, 2→6, 3→4, 4→3, ≥5→3 (max 4 per row)
    const mdCols = tiers.length >= 4 ? 3 : ((12 / tiers.length) as 12 | 6 | 4);
    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    {eyebrow && <Eyebrow text={eyebrow} />}
                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                        {heading}
                    </Typography>
                    {subheading && (
                        <Typography component="p" className="font-regular-text">
                            {subheading}
                        </Typography>
                    )}
                </Box>

                <Grid container spacing={3} alignItems="center">
                    {tiers.map((tier, idx) => (
                        <Grid item xs={12} sm={6} md={mdCols} key={idx} sx={gridItemFlexSx}>
                            {tier.featured ? (
                                <Box sx={darkCardFullWidthSx}>
                                    <Box sx={popularBadgeSx}>
                                        <Typography className="font-regular-text" sx={popularBadgeTextSx}>
                                            ⭐ MOST POPULAR
                                        </Typography>
                                    </Box>

                                    <Typography className="font-headers" sx={tierLabelDarkSx}>
                                        {tier.tier}
                                    </Typography>
                                    <Typography className="font-headers" sx={priceDarkSx}>
                                        {tier.price}
                                    </Typography>
                                    <Typography className="font-regular-text" sx={bestForDarkSx}>
                                        {tier.bestFor}
                                    </Typography>

                                    <Box display="flex" flexDirection="column" gap={spacing[16]} mb={spacing[40]} flex={1}>
                                        {tier.features.map((f, i) => (
                                            <FeatureItem key={i} text={f} dark />
                                        ))}
                                    </Box>

                                    <Button
                                        className="nft-button"
                                        fullWidth
                                        sx={orangeBtnSx}
                                        disableElevation
                                        {...getButtonProps(tier, openForm)}
                                    >
                                        {tier.buttonText}
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={lightCardFullWidthSx}>
                                    <Typography className="font-headers" sx={tierLabelLightSx}>
                                        {tier.tier}
                                    </Typography>
                                    <Typography className="font-headers" sx={priceLightSx}>
                                        {tier.price}
                                    </Typography>
                                    <Typography className="font-regular-text" sx={bestForLightSx}>
                                        {tier.bestFor}
                                    </Typography>

                                    <Box display="flex" flexDirection="column" gap={spacing[12]} mb={spacing[40]} flex={1}>
                                        {tier.features.map((f, i) => (
                                            <FeatureItem key={i} text={f} />
                                        ))}
                                    </Box>

                                    <Button
                                        className="secondary-button"
                                        fullWidth
                                        sx={lightBtnSx}
                                        disableElevation
                                        {...getButtonProps(tier, openForm)}
                                    >
                                        {tier.buttonText}
                                    </Button>
                                </Box>
                            )}
                        </Grid>
                    ))}
                </Grid>

                {trustItems && trustItems.length > 0 && (
                    <Box sx={trustStripSx}>
                        {trustItems.map((item, idx) => (
                            <Box key={idx} sx={{position: 'relative', pr: {md: idx < trustItems.length - 1 ? spacing[24] : 0}}}>
                                {idx < trustItems.length - 1 && <Box sx={trustDividerSx} />}
                                <Box sx={trustItemContentSx}>
                                    <Box sx={iconWrapSx}>
                                        <DisplayImage
                                            src={item.icon}
                                            alt={item.title}
                                            width={24}
                                            height={24}
                                            style={{width: spacing[24], height: spacing[24]}}
                                        />
                                    </Box>
                                    <Typography className="font-headers" sx={trustItemTitleSx}>
                                        {item.title}
                                    </Typography>
                                    <Typography className="font-regular-text" sx={trustItemBodySx}>
                                        {item.body}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </MUIContainer>
        </Box>
    );
};

export default memo(PricingSection);
